import type { SsoProvider } from '@outpost/api-client';
import { AlertTriangle, KeyRound } from 'lucide-react';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { Footer } from '@/components/footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { rememberSsoReturnTo, useAuth } from '@/lib/auth';
import { passkeysSupported } from '@/lib/webauthn';

/**
 * Sign in, or create the first account.
 *
 * Registration is offered on the same screen because a fresh self-hosted
 * install has no users at all, and a login form with no way in is a dead end.
 * Once an administrator switches registration off the offer disappears, since
 * the server would refuse it anyway.
 */
export function LoginPage() {
  const { user, client, login, completeMfa, loginWithPasskey, loginWithSso, register, loading } =
    useAuth();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [organization, setOrganization] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [allowRegistration, setAllowRegistration] = useState(false);
  const [ssoProviders, setSsoProviders] = useState<SsoProvider[]>([]);
  // Set by whoever sent the user here to sign in first: RequireAuth for an OAuth
  // consent request, which is useless without its query string, and the
  // invitation page, whose token is in the fragment. The SSO callback also
  // sends a challenge, when the provider accepted someone whose account has an
  // authenticator.
  const location = useLocation();
  const { from, mfaToken: pendingMfaToken } =
    (location.state as {
      from?: { pathname: string; search: string; hash?: string };
      mfaToken?: string;
    } | null) ?? {};
  const returnTo = from ? `${from.pathname}${from.search}${from.hash ?? ''}` : '/';
  // Set once the password or identity provider has been accepted and a code is
  // still needed. Until it is exchanged, nobody is signed in.
  const [mfaToken, setMfaToken] = useState<string | null>(pendingMfaToken ?? null);
  const [code, setCode] = useState('');

  useEffect(() => {
    let cancelled = false;
    client
      .authOptions()
      .then((options) => {
        if (cancelled) return;
        setAllowRegistration(options.allow_registration);
        setSsoProviders(options.sso_providers);
      })
      .catch(() => {
        // Offer it rather than risk the dead end above; the server still
        // decides, and says so if it is disabled.
        if (!cancelled) setAllowRegistration(true);
      });
    return () => {
      cancelled = true;
    };
  }, [client]);

  if (loading) return null;
  if (user) return <Navigate to={returnTo} replace />;

  const attempt = async (action: () => Promise<void>, fallback: string) => {
    setError(null);
    setSubmitting(true);
    try {
      await action();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : fallback);
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    await attempt(async () => {
      if (mode === 'login') {
        const challenge = await login(email, password);
        if (challenge) {
          setMfaToken(challenge.mfaToken);
          setCode('');
        }
      } else {
        await register({ email, name, password, organization: organization || undefined });
      }
    }, 'Sign in failed');
  };

  const onSubmitCode = async (event: FormEvent) => {
    event.preventDefault();
    if (!mfaToken) return;
    await attempt(() => completeMfa(mfaToken, code), 'That code was not accepted');
  };

  // A passkey verifies the person as well as the device, so it is the whole
  // sign-in: there is never a code to follow.
  const onPasskey = () => void attempt(() => loginWithPasskey(), 'Passkey sign-in failed');

  // The browser leaves this page entirely, so where it was headed has to
  // survive the round trip through the provider.
  const onSso = (provider: SsoProvider) =>
    void attempt(() => {
      rememberSsoReturnTo(returnTo);
      return loginWithSso(provider.id);
    }, `Could not start signing in with ${provider.name}`);

  if (mfaToken) {
    return (
      <Shell
        title="Two-factor authentication"
        description="Enter the code from your authenticator app."
      >
        <form onSubmit={onSubmitCode} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="code">Authentication code</Label>
            <Input
              id="code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              required
              autoFocus
              inputMode="text"
              autoComplete="one-time-code"
              placeholder="123456"
            />
            <p className="text-xs text-muted-foreground">
              Lost your phone? Enter one of your recovery codes instead.
            </p>
          </div>

          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}

          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? 'Please wait…' : 'Sign in'}
          </Button>
          <button
            type="button"
            className="w-full text-sm text-muted-foreground hover:text-foreground"
            onClick={() => {
              setMfaToken(null);
              setError(null);
              setPassword('');
            }}
          >
            Back to sign in
          </button>
        </form>
      </Shell>
    );
  }

  return (
    <Shell
      title={mode === 'login' ? 'Sign in to Outpost' : 'Create your account'}
      description={
        mode === 'login'
          ? 'Error monitoring for your applications.'
          : 'The first account becomes the owner of a new organization.'
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        {mode === 'register' && (
          <div className="space-y-1.5">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              autoComplete="name"
            />
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            autoComplete="email"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            minLength={mode === 'register' ? 12 : undefined}
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          />
          {mode === 'register' && (
            <p className="text-xs text-muted-foreground">At least 12 characters.</p>
          )}
        </div>

        {mode === 'register' && (
          <div className="space-y-1.5">
            <Label htmlFor="organization">Organization</Label>
            <Input
              id="organization"
              value={organization}
              onChange={(event) => setOrganization(event.target.value)}
              placeholder="Acme"
            />
          </div>
        )}

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? 'Please wait…' : mode === 'login' ? 'Sign in' : 'Create account'}
        </Button>

        {mode === 'login' && passkeysSupported() && (
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={onPasskey}
            disabled={submitting}
          >
            <KeyRound />
            Sign in with a passkey
          </Button>
        )}

        {mode === 'login' &&
          ssoProviders.map((provider) => (
            <Button
              key={provider.id}
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => onSso(provider)}
              disabled={submitting}
            >
              Continue with {provider.name}
            </Button>
          ))}

        {(allowRegistration || mode === 'register') && (
          <button
            type="button"
            className="w-full text-sm text-muted-foreground hover:text-foreground"
            onClick={() => {
              setMode(mode === 'login' ? 'register' : 'login');
              setError(null);
            }}
          >
            {mode === 'login'
              ? 'First time here? Create an account'
              : 'Already have an account? Sign in'}
          </button>
        )}
      </form>
    </Shell>
  );
}

/** The sign-in page's chrome, shared by the password step and the code step. */
function Shell({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-screen flex-col px-4">
      <main className="grid flex-1 place-items-center py-8">
        <Card className="w-full max-w-sm">
          <CardHeader className="items-center text-center">
            <span className="grid size-10 place-items-center rounded-lg bg-primary text-primary-foreground">
              <AlertTriangle className="size-5" />
            </span>
            <CardTitle className="text-xl">{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </CardHeader>
          <CardContent>{children}</CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
}
